// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { type Shipment, type ShipmentItem, useEvolveDelete, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import { Button, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { DetailSection } from 'components/Mantine/DetailPage/DetailSection';
import { VariableTrackingModal } from 'components/Mantine/Modals/VariableTrackingModal';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { AddShipmentItemsModal } from 'modules/Shop/Shipments/ShipmentDetail/AddShipmentItemsModal/AddShipmentItemsModal';
import { ShipmentItemRow } from 'modules/Shop/Shipments/ShipmentDetail/ShipmentItemsList/ShipmentItemRow';
import { useCanEditShipment } from 'modules/Shop/Shipments/ShipmentDetail/useCanEditShipment';

type Props = {
  shipment: Shipment;
  onItemsUpdated: () => void;
};

export const ShipmentItemsList = ({ shipment, onItemsUpdated }: Props) => {
  const { canEditShipment } = useCanEditShipment(shipment);
  const canEdit = canEditShipment('addEditDeleteItems');

  const [deleteItem, setDeleteItem] = useState<ShipmentItem | null>(null);
  const [addItemsOpened, { open: openAddItemsModal, close: closeAddItemsModal }] = useDisclosure(false);

  const { apiCall: deleteShipmentItemApiCall } = useEvolveDelete('shop/shipment/:shipmentId/item/:shipmentItemId');

  const {
    data: items,
    fetchNextPage,
    loading,
    entireCount,
    reset: resetItems,
  } = useEvolvePaginatedGet('shop/shipment/:shipmentId/item', {
    defaultConfig: { url: `shop/shipment/${shipment.shipmentId}/item` },
  });

  const handleItemsChanged = () => {
    resetItems();
    onItemsUpdated();
  };

  return (
    <>
      <DetailSection
        title="Shipping Items"
        large
        noContentMessage="No items added to this shipping order."
        showNoContentMessage={!loading && entireCount === 0}
        rightSection={
          canEdit && (
            <Button size="xs" variant="light" onClick={openAddItemsModal}>
              Add item
            </Button>
          )
        }
      >
        {items.map((item) => (
          <ShipmentItemRow
            key={item.shipmentItemId}
            item={item}
            canEdit={canEdit}
            onDelete={setDeleteItem}
            onItemUpdated={handleItemsChanged}
          />
        ))}
        <TriggerOnVisible onVisible={fetchNextPage} loading={loading} />
      </DetailSection>

      <AddShipmentItemsModal
        shipment={shipment}
        opened={addItemsOpened}
        onClose={closeAddItemsModal}
        onItemsAdded={handleItemsChanged}
      />

      <VariableTrackingModal
        title="Remove item from shipment"
        variable={deleteItem}
        onClose={() => setDeleteItem(null)}
        onConfirm={async (itemToDelete) => {
          await deleteShipmentItemApiCall({
            url: `shop/shipment/${itemToDelete.shipmentId}/item/${itemToDelete.shipmentItemId}`,
          });
          handleItemsChanged();
        }}
        buttonColor="red"
        confirmationText="Remove"
      >
        {(itemToDelete) => (
          <Text>
            Remove <b>{itemToDelete.workOrderItemName}</b> from shipping order <b>{shipment.name}</b>?
          </Text>
        )}
      </VariableTrackingModal>
    </>
  );
};
